import { jsPDF } from "jspdf";
import {
  formatDateForPdf,
  formatPhone,
  formatSupplyVetCode,
  formatReminder,
  formatWeight,
  formatRation,
} from "@/utils/pdfUtils.js";
import { RationDataAdapter } from "./RationDataAdapter.js";

export class JsPdfService {
  constructor() {
    this.adapter = new RationDataAdapter();
  }

  async generateRationPdf(data, filename = "ration.pdf") {
    try {
      const safeData = await this.adapter.adaptForPdf(data, "CALCULATE");

      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
      });

      const imgProductFinal = safeData.calculation.variantImage || this.getPlaceholderImage();
      const qrCodeFinal = await this.loadLocalQRCode() || "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";

      // Définition des couleurs
      const mainTextColor = "#003366";
      const lightGreenBgColor = "#E2F0D9";
      const blackColor = "#000000";

      // --- Section 1 : Informations du vétérinaire (en-tête gauche) ---
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(mainTextColor);
      doc.setFontSize(11);
      doc.text(safeData.veterinary.name, 15, 10);
      doc.text(safeData.veterinary.street, 15, 16);
      doc.text(safeData.veterinary.postalCodeCity, 15, 22);

      doc.setFontSize(10);
      doc.text(`Tél. ${formatPhone(safeData.veterinary.phone)}`, 15, 32);
      doc.text(safeData.veterinary.email, 15, 37);
      doc.text(`Code supplyvet : ${formatSupplyVetCode(safeData.veterinary.supplyvetCode)}`, 15, 47);

      // --- Section 2 : Informations de la visite (en-tête droit) ---
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(blackColor);
      doc.setFontSize(10);
      doc.text(`Date de visite : ${formatDateForPdf(safeData.dates.visitDate)}`, 140, 10);

      if (safeData.dates.nextVisitDate) {
        doc.text(`Prochaine visite : ${formatDateForPdf(safeData.dates.nextVisitDate)}`, 140, 16);
      }

      doc.text(`Rappel à J-7 ? ${formatReminder(safeData.dates.reminderTypes)}`, 140, 22);

      // --- Section 3 : Informations du propriétaire ---
      doc.setFont("Helvetica", "bold");
      doc.text(safeData.owner.fullName, 140, 43);
      doc.setFont("Helvetica", "normal");
      doc.text(safeData.owner.email, 140, 49);
      doc.text(formatPhone(safeData.owner.phone), 140, 55);

      // --- Section 4 : Informations de l'animal (encadré) ---
      doc.setDrawColor(mainTextColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(30, 75, 150, 40, 3, 3, "D"); // ← largeur augmentée à 150mm

      // Nom de l'animal centré
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(mainTextColor);
      doc.text(safeData.animal.name, 105, 84, { align: "center" });

      // Détails de l'animal avec espacement propre et wrap automatique
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(blackColor);

      let animalInfoY = 92;
      const lineHeight = 5.5;
      const maxWidth = 140;

      const addWrappedText = (text, bold = false) => {
        if (bold) doc.setFont("Helvetica", "bold");
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          doc.text(line, 35, animalInfoY);
          animalInfoY += lineHeight;
        });
        if (bold) doc.setFont("Helvetica", "normal");
      };

      // Poids idéal (toujours affiché)
      addWrappedText(`${safeData.animal.idealWeightLabel} : ${formatWeight(safeData.animal.idealWeight)}`, true);

      if (safeData.animal.dailyActivity) {
        addWrappedText(`Activité quotidienne : ${safeData.animal.dailyActivity}`);
      }

      if (safeData.animal.physiologicalStage) {
        addWrappedText(`Stade physiologique : ${safeData.animal.physiologicalStage}`);
      }

      if (safeData.animal.weightManagement) {
        addWrappedText(`Gestion du poids : ${safeData.animal.weightManagement}`);
      }

      // --- Section 5 : Titre "Programme nutritionnel" ---
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(mainTextColor);
      doc.text("Programme nutritionnel", 15, 140);

      // --- Section 6 : Image du produit ---
      doc.addImage(imgProductFinal, "WEBP", 20, 160, 60, 60);

      // --- Section 7 : Détails du produit ---
      doc.setFontSize(10);
      doc.setTextColor(blackColor);

      const referenceText = `Référence : ${safeData.calculation.reference}`;
      const referenceLines = doc.splitTextToSize(referenceText, 95);

      let refYPos = 160;
      referenceLines.forEach((line, index) => {
        doc.text(line, 95, refYPos + index * 5);
      });

      // Boîte verte pour les détails de la ration
      const greenBoxY = 170;
      doc.setFillColor(lightGreenBgColor);
      doc.roundedRect(95, greenBoxY, 95, 35, 3, 3, "F");

      doc.setFont("Helvetica", "normal");
      doc.text("Ration journalière :", 100, greenBoxY + 10);
      doc.setFont("Helvetica", "bold");
      doc.text(formatRation(safeData.calculation.dailyRation), 140, greenBoxY + 10);

      if (safeData.calculation.dailyCost !== "") {
        doc.setFont("Helvetica", "normal");
        doc.text("Coût journalier :", 100, greenBoxY + 18);
        doc.setFont("Helvetica", "bold");
        doc.text(safeData.calculation.dailyCost, 140, greenBoxY + 18);

        doc.setFont("Helvetica", "normal");
        doc.text("Durée du sac :", 100, greenBoxY + 26);
        doc.setFont("Helvetica", "bold");
        doc.text(safeData.calculation.bagDuration, 140, greenBoxY + 26);
      } else {
        doc.setFont("Helvetica", "normal");
        doc.text("Durée du sac :", 100, greenBoxY + 18);
        doc.setFont("Helvetica", "bold");
        doc.text(safeData.calculation.bagDuration, 140, greenBoxY + 18);
      }

      // --- Section 8 : Instruction de transition ---
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(blackColor);
      const instructionText =
        "Servir progressivement l'aliment en ménageant une transition de quelques jours avec l'alimentation précédente.";
      doc.text(instructionText, 15, 225, { maxWidth: 180 });

      // --- Section 9 : QR Code ---
      doc.addImage(qrCodeFinal, "PNG", 160, 235, 30, 30);

      // --- Pied de page ---
      doc.setFillColor(mainTextColor);
      doc.rect(0, 282, 210, 15, "F");
      doc.setFontSize(12);
      doc.setTextColor("#FFFFFF");
      doc.text("www.supplyvet.com", 105, 292, { align: "center" });

      // --- Téléchargement ---
      doc.save(filename);
      return true;
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      return false;
    }
  }

  async loadLocalQRCode() {
    try {
      const qrCodeUrl = require("@/assets/qrcode.png");
      return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.src = qrCodeUrl;
      });
    } catch (error) {
      console.error("Impossible de charger le QR code local:", error);
      return null;
    }
  }

  getPlaceholderImage() {
    return (
      "data:image/svg+xml;base64," +
      btoa(
        `<svg width="200" height="200" xmlns="http://www.w3.org/2000/svg"><rect width="200" height="200" fill="#f0f0f0"/><text x="100" y="100" font-family="Arial" font-size="14" fill="#888" text-anchor="middle">Image non disponible</text></svg>`
      )
    );
  }
}